import styled, { CSSObject } from "@emotion/styled";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { convertSpacerValueToNumeric } from "@brexhq/next/src/components/layout/Spacers/Spacers";
import { SpacingValues } from "@brexhq/shared/types/SpacingScale";
import { AlignmentOptions } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowsModule";

const textAlignmentToFlex = {
  left: "flex-start",
  center: "space-between",
  right: "flex-end",
};

export const BlockWrapper = styled(Container)<{
  maxWidth?: number;
  widthStyle: "spaceEvenly" | "custom";
  gap: SpacingValues;
  numberOfBlocks: number;
  directionBreakpoint: "none" | "mobile" | "tablet";
}>(
  ({
    theme,
    maxWidth,
    widthStyle,
    gap,
    numberOfBlocks,
    directionBreakpoint,
  }) => {
    const blockWidth = (gap: number) =>
      widthStyle === "spaceEvenly"
        ? `calc(calc(100% - ${gap * numberOfBlocks}px)/${numberOfBlocks})`
        : "auto";
    const blockMaxWidth =
      widthStyle === "custom" && maxWidth ? maxWidth : "none";

    return {
      maxWidth: blockMaxWidth,
      width: blockWidth(convertSpacerValueToNumeric(gap, "LRG")),
      [theme.breakpoints.MED]: {
        width:
          directionBreakpoint === "tablet"
            ? "100%"
            : blockWidth(convertSpacerValueToNumeric(gap, "MED")),
        maxWidth: directionBreakpoint === "tablet" ? "none" : blockMaxWidth,
      },
      [theme.breakpoints.SML]: {
        // if directionBreakpoint is "none", blocks are always stacked horizontally
        width:
          directionBreakpoint === "none"
            ? blockWidth(convertSpacerValueToNumeric(gap, "SML"))
            : "100%",
        maxWidth: directionBreakpoint === "none" ? blockMaxWidth : "none",
      },
    };
  },
);

export const HorizontalBlocksContainer = styled.div<{
  horizontalAlignment: AlignmentOptions;
  verticalAlignment: "flex-start" | "flex-end" | "center";
  directionBreakpoint: "none" | "mobile" | "tablet";
  widthStyle: "spaceEvenly" | "custom";
  gap: SpacingValues;
}>(
  ({
    theme,
    horizontalAlignment,
    verticalAlignment,
    directionBreakpoint,
    widthStyle,
    gap,
  }) => [
    {
      display: "flex",
      width: "100%",
      alignItems: verticalAlignment,
      justifyContent: textAlignmentToFlex[horizontalAlignment],
      flexDirection: "row",
      flexWrap:
        directionBreakpoint === "none" || widthStyle === "custom"
          ? "nowrap"
          : "wrap",
      gap: convertSpacerValueToNumeric(gap, "LRG"),
      [theme.breakpoints.MED]: {
        gap: convertSpacerValueToNumeric(gap, "MED"),
      },
      [theme.breakpoints.SML]: {
        gap: convertSpacerValueToNumeric(gap, "SML"),
      },
    },
    // These styles are added at the appropriate breakpoint depending on the directionBreakpoint selection, which determines when the content should shift to columns.
    ({ horizontalAlignment, verticalAlignment }) => {
      const columnStyles: CSSObject = {
        flexDirection: "column",
        alignItems:
          horizontalAlignment === "center"
            ? "center"
            : textAlignmentToFlex[horizontalAlignment],
        justifyContent:
          verticalAlignment === "center" ? "space-between" : verticalAlignment,
      };

      switch (directionBreakpoint) {
        case "none":
          return;
        case "tablet":
          return {
            [theme.breakpoints.MED]: {
              ...columnStyles,
            },
          };
        case "mobile":
          return {
            [theme.breakpoints.SML]: {
              ...columnStyles,
            },
          };
      }
    },
  ],
);

export const RowBlocks = styled(HorizontalBlocksContainer)(
  ({ theme, directionBreakpoint }) => {
    switch (directionBreakpoint) {
      case "none":
        return;
      case "tablet":
        return {
          [theme.breakpoints.MED]: {
            display: "none",
          },
        };
      case "mobile":
        return {
          [theme.breakpoints.SML]: {
            display: "none",
          },
        };
    }
  },
);

export const ColumnBlocks = styled(HorizontalBlocksContainer)(
  ({ theme, directionBreakpoint }) => [
    {
      display: "none",
    },
    () => {
      switch (directionBreakpoint) {
        case "none":
          return;
        case "tablet":
          return {
            [theme.breakpoints.MED]: {
              display: "flex",
            },
          };
        case "mobile":
          return {
            [theme.breakpoints.SML]: {
              display: "flex",
            },
          };
      }
    },
  ],
);
